import React, { useState, useContext, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { AuthContext } from '../AuthContext';
// Prepričaj se, da je Login.css uvožen, da primejo stili
import '../Pages/Login.css';

const AuthModal = ({ show, onHide, onSuccess }) => {
    // --- State ---
    const [isRegister, setIsRegister] = useState(false);
    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [username, setUsername] = useState('');

    // --- Statusi ---
    const [usernameStatus, setUsernameStatus] = useState('idle');
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    // --- Vidnost gesla ---
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const { loginUser } = useContext(AuthContext);

    // Reset ob odprtju
    useEffect(() => {
        if (show) {
            setError(null);
            setSuccessMsg(null);
            setIdentifier('');
            setPassword('');
            setConfirmPassword('');
            setUsername('');
            setIsRegister(false); // Privzeto prijava
        }
    }, [show]);

    // Preverjanje uporabniškega imena (kopirano iz Login.js)
    useEffect(() => {
        if (!isRegister || username.trim().length < 3) {
            setUsernameStatus('idle');
            return;
        }
        const controller = new AbortController();
        const timeout = setTimeout(() => {
            setUsernameStatus('checking');
            fetch(`${process.env.REACT_APP_API_URL}/auth/check-username?username=${encodeURIComponent(username.trim())}`, {
                signal: controller.signal,
            })
                .then(res => res.json())
                .then(data => {
                    setUsernameStatus(data.available ? 'available' : 'taken');
                })
                .catch((err) => {
                    if (err.name !== 'AbortError') setUsernameStatus('idle');
                });
        }, 500);
        return () => {
            clearTimeout(timeout);
            controller.abort();
        };
    }, [username, isRegister]);

    const handleSocialLogin = (provider) => {
        localStorage.setItem('loginRedirect', window.location.pathname);
        localStorage.setItem('autoSubmitAd', 'true'); // Da se oglas odda, če pride nazaj
        window.location.href = `${process.env.REACT_APP_API_URL}/auth/${provider}`;
    };

    const toggleForm = () => {
        setIsRegister(!isRegister);
        setError(null);
        setSuccessMsg(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccessMsg(null);

        if (isRegister && password !== confirmPassword) {
            setError('Gesli se ne ujemata.');
            return;
        }
        if (isRegister && usernameStatus === 'taken') {
            setError('Uporabniško ime je že zasedeno.');
            return;
        }

        const url = isRegister
            ? `${process.env.REACT_APP_API_URL}/user/register`
            : `${process.env.REACT_APP_API_URL}/auth/login`;

        const payload = isRegister
            ? { username, email: identifier, password }
            : { identifier, password };

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include',
            });

            const data = await response.json();

            if (response.ok) {
                // Če dobimo userja (login ali soft-registration)
                if (data.user) {
                    loginUser(data.user);
                    if (onSuccess) onSuccess(data.user);
                    onHide();
                } else {
                    // Fallback (če ni auto-login)
                    if (isRegister) setSuccessMsg('Registracija uspešna! Preverite email.');
                }
            } else {
                if (data.field === 'email') setError('Ta email je že v uporabi.');
                else if (data.field === 'username') setError('To uporabniško ime je že zasedeno.');
                else setError(data.message || 'Prišlo je do napake.');
            }
        } catch (err) {
            setError('Napaka pri povezavi s strežnikom.');
        }
    };

    // Helper za moč gesla
    const calculatePasswordStrength = (password) => {
        let score = 0;
        if (!password) return { score: 0, text: '', color: '' };
        if (password.length >= 8) score++;
        if (password.length >= 12) score++;
        if (/[a-z]/.test(password)) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[^A-Za-z0-9]/.test(password)) score++;
        let strengthLevel = Math.min(Math.floor(score / 1.5), 3);
        switch (strengthLevel) {
            case 0: return { score: 1, text: 'Šibko', color: '#FF4136' };
            case 1: return { score: 2, text: 'Srednje', color: '#FF851B' };
            case 2: return { score: 3, text: 'Močno', color: '#2ECC40' };
            case 3: return { score: 4, text: 'Zelo močno', color: '#0074D9' };
            default: return { score: 0, text: '', color: '' };
        }
    };
    const passwordStrength = isRegister ? calculatePasswordStrength(password) : { score: 0, text: '' };
    const passwordsMatch = password === confirmPassword;

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            backdrop="static"
            // Uporabimo custom class za override bootstrap stilov
            dialogClassName="custom-auth-modal"
            backdropClassName="custom-light-backdrop" 
        >
            {/* Tukaj NE uporabimo Modal.Header/Body, ampak direktno tvojo strukturo */}

            <div className="sign_in_form" style={{ position: 'relative' }}>
                {/* Gumb za zapiranje modala (X) - dodan ročno, ker smo skrili Modal.Header */}
                <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={onHide}
                    style={{ position: 'absolute', top: '15px', right: '15px', zIndex: 10 }}
                ></button>

                <div className="sign_title">
                    <h5 className="title">{isRegister ? 'Registracija' : 'Prijava za objavo'}</h5>
                </div>

                <div className="social-login-buttons mb-4 mt-3">
                    <button className="btn btn-social google-btn w-100 mb-2" onClick={() => handleSocialLogin('google')}>
                        <i className="fab fa-google me-2"></i> Nadaljuj z Google
                    </button>
                    <button className="btn btn-social facebook-btn w-100" onClick={() => handleSocialLogin('facebook')}>
                        <i className="fab fa-facebook-f me-2"></i> Nadaljuj s Facebook
                    </button>
                </div>

                <div className="social-login-divider">
                    <span className="divider-line"></span>
                    <span className="divider-text">ALI Z EMAILOM</span>
                    <span className="divider-line"></span>
                </div>

                {error && <div className="alert alert-danger">{error}</div>}
                {successMsg && <div className="alert alert-success">{successMsg}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="sign_form_wrapper">

                        {isRegister && (
                            <div className="single_form_username">
                                <input
                                    type="text"
                                    placeholder="Uporabniško ime"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                />
                                <i className="fa fa-user input-icon-left"></i>
                                {username && (
                                    <div className="username-status-message" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                        {usernameStatus === 'checking' && <span style={{ color: '#888' }}>Preverjam...</span>}
                                        {usernameStatus === 'available' && <span style={{ color: 'green' }}>Na voljo ✅</span>}
                                        {usernameStatus === 'taken' && <span style={{ color: 'red' }}>Zasedeno ❌</span>}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="single_form">
                            <input
                                type="text"
                                placeholder={isRegister ? "Email" : "Email ali uporabniško ime"}
                                value={identifier}
                                onChange={(e) => setIdentifier(e.target.value)}
                                required
                            />
                            <i className="fa fa-envelope input-icon-left"></i>
                        </div>

                        <div className="single_form password-input-container">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Geslo"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <i className="fa fa-key input-icon-left"></i>
                            <i
                                className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'} password-toggle-icon-right`}
                                onClick={() => setShowPassword(!showPassword)}
                                style={{ cursor: 'pointer' }}
                            ></i>
                        </div>

                        {isRegister && password && (
                            <div className="password-strength-container">
                                <div className="password-strength-bar">
                                    <div className="password-strength-fill" style={{ width: `${(passwordStrength.score / 4) * 100}%`, backgroundColor: passwordStrength.color }}></div>
                                </div>
                                <span className="password-strength-text" style={{ color: passwordStrength.color }}>{passwordStrength.text}</span>
                            </div>
                        )}

                        {isRegister && (
                            <>
                                <div className="single_form password-input-container">
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder="Potrdi geslo"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                    />
                                    <i className="fa fa-key input-icon-left"></i>
                                    <i
                                        className={`fa ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} password-toggle-icon-right`}
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        style={{ cursor: 'pointer' }}
                                    ></i>
                                </div>
                                {confirmPassword && (
                                    <small className={passwordsMatch ? 'text-success' : 'text-danger'}>
                                        {passwordsMatch ? 'Gesli se ujemata' : 'Gesli se ne ujemata'}
                                    </small>
                                )}
                            </>
                        )}

                        <div className="single_form mt-3">
                            <button type="submit" className="btn btn-success post-item-navbar-btn w-100">
                                {isRegister ? 'Registriraj se' : 'Prijavi se in objavi'}
                            </button>
                        </div>

                        <div className="text-center mt-3">
                            <small>
                                {isRegister ? 'Že imate račun? ' : "Še nimate računa? "}
                                <button className="btn btn-link p-0" type="button" onClick={toggleForm} style={{ textDecoration: 'none' }}>
                                    {isRegister ? 'Prijava' : 'Registracija'}
                                </button>
                            </small>
                        </div>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default AuthModal;